import axios from "axios";
import React, { useEffect, useCallback, useState } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import Select from "react-select";
import { GPL_HIGH_VALUE_THRESHOLD, GPL_INSURANCE_BASE_TENURE } from "../../../utils/utils";
import "./GenerateDocumentBranchManagerModal.css";

const GenerateDocumentBranchManagerModal = ({
  show,
  handleClose,
  handleConfirm,
  message,
  proposedLoanAmount,
  setProposedLoanAmount,
  sanctionedLoanAmount,
  setSanctionedLoanAmount,
  processingCharge,
  setProcessingCharge,
  isProcessingChargePaid,
  setIsProcessingChargePaid,
  gst,
  setGst,
  isGstPaid,
  setIsGstPaid,
  securityDeposit,
  setSecurityDeposit,
  isSecurityDepositPaid,
  setIsSecurityDepositPaid,
  securityDeposit1,
  setSecurityDeposit1,
  isSecurityDeposit1Paid,
  setIsSecurityDeposit1Paid,
  isLoanInsured,
  setIsLoanInsured,
  insuranceAmount,
  setInsuranceAmount,
  isInsuranceAmountPaid,
  setIsInsuranceAmountPaid,
  amountToRelease,
  setAmountToRelease,
  emiDateByBranchManager,
  setEmiDateByBranchManager,
  installmentStartDate,
  setInstallmentStartDate,
  disbursementDate,
  fundingAgencyData,
  loanType,
  ornamentModelNoByBm,
  setOrnamentModelNoByBm,
  ornamentNameByBm,
  setOrnamentNameByBm,
  ornamentWeightByBm,
  setOrnamentWeightByBm,
  wastageByBm,
  setWastageByBm,
  gstByBm,
  setGstByBm,
  goldRateByBm,
  setGoldRateByBm,
  ornamentWeightValueByBm,
  wastageValueByBm,
  totalByBm,
  gstValueByBm,
  eligibleLoanAmountByBm,
  totalLoanAmountByBm,
  setTotalLoanAmountByBm,
  needCashLoan,
  setNeedCashLoan,
  cashLoanAmount,
  setCashLoanAmount,
  tenureInMonths,
}) => {
  const [validationError, setValidationError] = useState("");

  const handleClick = () => {
    // Check if insurance is enabled by the funding agency
    const isInsuranceEnabledByAgency = loanType === "Business Loan"
      ? (!fundingAgencyData || fundingAgencyData.blInsuranceEnabled !== false)
      : (!fundingAgencyData || fundingAgencyData.gplInsuranceEnabled !== false);

    if (
      (loanType === "Business Loan" &&
        isInsuranceEnabledByAgency &&
        isLoanInsured &&
        (!insuranceAmount || insuranceAmount === 0)) ||
      (loanType === "GP Loan" &&
        (!ornamentModelNoByBm ||
          !ornamentNameByBm ||
          !ornamentWeightByBm ||
          !wastageByBm ||
          !gstByBm ||
          (needCashLoan && (!cashLoanAmount || cashLoanAmount === 0)))) ||
      !emiDateByBranchManager
    ) {
      setValidationError("Please fill all required fields");
    } else {
      setValidationError("");
      handleConfirm();
    }
  };

  const calculateLoanAmountToRelease = useCallback(() => {
    let releaseAmount = Number(sanctionedLoanAmount) || 0;

    // A GP Loan with no cash component hands the customer no money at all - the
    // whole loan is gold being bought for them. There is therefore nothing to
    // net the fee, its GST or the insurance out of, so those three are not
    // subtracted. The security deposit is deliberately still deducted: the
    // client's rule named only the other three.
    const isGoldOnlyGpl =
      loanType === "GP Loan" && !(needCashLoan && Number(cashLoanAmount) > 0);

    if (!isProcessingChargePaid && !isGoldOnlyGpl) {
      releaseAmount -= Number(processingCharge) || 0;
    }

    if (!isGstPaid && !isGoldOnlyGpl) {
      releaseAmount -= Number(gst) || 0;
    }

    if (!isSecurityDepositPaid) {
      releaseAmount -= Number(securityDeposit) || 0;
    }

    if (!isSecurityDeposit1Paid) {
      releaseAmount -= Number(securityDeposit1) || 0;
    }




    if (isLoanInsured && !isInsuranceAmountPaid && !isGoldOnlyGpl) {
      releaseAmount -= Number(insuranceAmount) || 0;
    }

    releaseAmount = releaseAmount < 0 ? 0 : releaseAmount;
    setAmountToRelease(releaseAmount);
  }, [
    loanType,
    needCashLoan,
    cashLoanAmount,
    sanctionedLoanAmount,
    isProcessingChargePaid,
    processingCharge,
    isGstPaid,
    gst,
    isSecurityDepositPaid,
    securityDeposit,
    isSecurityDeposit1Paid,
    securityDeposit1,
    isLoanInsured,
    isInsuranceAmountPaid,
    insuranceAmount,
    setAmountToRelease,
  ]);

  useEffect(() => {
    calculateLoanAmountToRelease();
  }, [calculateLoanAmountToRelease]);

  // EMI day: clamp to 1-31 as the value is typed, rather than only at submit,
  // so an out-of-range number is never briefly stored in state.
  const handleEmiDayChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setEmiDateByBranchManager("");
      return;
    }
    const day = Math.max(1, Math.min(31, parseInt(raw, 10) || 1));
    setEmiDateByBranchManager(String(day));
  };

  // Block characters a day-of-month field has no use for. Paste and browser
  // autofill are still caught by the clamp in handleEmiDayChange.
  const blockNonDayKeys = (e) => {
    if (["-", "+", "e", "E", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  // The first instalment may be set from the disbursement date up to 3 months later.
  const installmentDateMin = disbursementDate
    ? new Date(disbursementDate).toISOString().split("T")[0]
    : undefined;
  const installmentDateMax = disbursementDate
    ? (() => {
      const limit = new Date(disbursementDate);
      limit.setMonth(limit.getMonth() + 3);
      return limit.toISOString().split("T")[0];
    })()
    : undefined;

  // ---- GP Loan layout --------------------------------------------------------
  // Presentation only: every value, setter, show/hide rule and check below is
  // the same one the old stacked GP Loan form used.
  const isGpl = loanType === "GP Loan";

  const showGplCashAndInsurance =
    !fundingAgencyData ||
    fundingAgencyData.gplInsuranceEnabled !== false ||
    (totalLoanAmountByBm >= GPL_HIGH_VALUE_THRESHOLD &&
      fundingAgencyData.gplInsuranceAbove199999Enabled !== false);
  const showGplSecurityDeposit =
    !fundingAgencyData || fundingAgencyData.gplSecurityDepositEnabled !== false;

  // The premium is scaled by the tenure, so the label has to quote the rate
  // actually being charged - the configured percentage alone would contradict
  // the amount next to it.
  const gplInsuranceRate = (() => {
    const isHighValue = totalLoanAmountByBm >= GPL_HIGH_VALUE_THRESHOLD;
    const configuredRate = isHighValue
      ? fundingAgencyData?.gplInsuranceAbove199999Percentage || 1.142
      : fundingAgencyData?.gplInsurancePercentage || 1.142;
    const months = Number(tenureInMonths);
    const factor =
      Number.isFinite(months) && months > 0 ? months / GPL_INSURANCE_BASE_TENURE : 1;
    const effectiveRate = Number((configuredRate * factor).toFixed(3));
    const gstSuffix =
      isHighValue && fundingAgencyData?.gplGstEnabled !== false
        ? ` + ${fundingAgencyData?.gplGstPercentage || 18}% GST`
        : "";
    return `${effectiveRate}%${gstSuffix}`;
  })();

  const inr = (value) => `₹${Math.round(Number(value) || 0).toLocaleString("en-IN")}`;
  const required = <span className="gdoc-required">*</span>;

  // Scrolling over a focused number box silently changed the number; dropping
  // focus stops that without blocking the page from scrolling.
  const numberGuards = {
    onWheel: (e) => e.currentTarget.blur(),
    onKeyDown: (e) => {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
    },
  };

  const unitInput = (id, unit, value, onChange, placeholder, unitOnLeft = false) => (
    <div className={`gdoc-input-unit ${unitOnLeft ? "gdoc-unit-on-left" : ""}`}>
      <Form.Control
        id={id}
        type="number"
        min="0"
        step="any"
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        {...numberGuards}
      />
      <span className="gdoc-unit">{unit}</span>
    </div>
  );

  // One read-only invoice line: the booking figure and the amount it gives.
  const valueRow = (label, hint, bookingValue, amount) => (
    <tr>
      <th scope="row">
        <span className="gdoc-row-label">{label}</span>
        {hint && <span className="gdoc-hint">{hint}</span>}
      </th>
      <td className="gdoc-col-value">{bookingValue}</td>
      <td className="gdoc-num">{amount}</td>
    </tr>
  );

  // One charge line: the amount and whether the customer already paid it.
  const chargeRow = (label, hint, amount, paid, setPaid) => (
    <tr>
      <th scope="row">
        {label}
        {hint && <span className="gdoc-hint">{hint}</span>}
      </th>
      <td className="gdoc-col-paid">
        {setPaid ? (
          <Form.Check
            type="checkbox"
            checked={paid}
            onChange={(e) => setPaid(e.target.checked)}
            aria-label={`${label} paid`}
          />
        ) : (
          <span className="gdoc-muted">–</span>
        )}
      </td>
      <td className="gdoc-num">{inr(amount)}</td>
    </tr>
  );

  const gplBody = (
    <>
      <div className="gdoc-layout">
        <section className="gdoc-col">
          <h6 className="gdoc-section">
            Ornament and eligible amount
            <span className="gdoc-section-note">From the Branch Manager&apos;s booking</span>
          </h6>
          {/* The ornament figures are entered by the Branch Manager in the
              booking process. They are shown here as text only, so the loan
              amount cannot be changed at this step. */}
          <div className="gdoc-pair">
            <div className="gdoc-field">
              <span className="gdoc-label">Ornament model number</span>
              <div className="gdoc-readonly">{ornamentModelNoByBm || "–"}</div>
            </div>
            <div className="gdoc-field">
              <span className="gdoc-label">Ornament name</span>
              <div className="gdoc-readonly">{ornamentNameByBm || "–"}</div>
            </div>
          </div>

          <table className="gdoc-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="gdoc-col-value">Booking</th>
                <th className="gdoc-num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {valueRow(
                "Gold rate (22k)",
                "Per gram",
                `${inr(goldRateByBm)} / g`,
                <span className="gdoc-muted">–</span>
              )}
              {valueRow(
                "Ornament weight",
                "Grams × gold rate",
                `${Number(ornamentWeightByBm) || 0} g`,
                inr(ornamentWeightValueByBm)
              )}
              {valueRow(
                "Wastage",
                "% of gold value",
                `${Number(wastageByBm) || 0} %`,
                inr(wastageValueByBm)
              )}
              <tr className="gdoc-subtotal">
                <th scope="row">Gold value + wastage</th>
                <td />
                <td className="gdoc-num">{inr(totalByBm)}</td>
              </tr>
              {valueRow(
                "GST",
                "% of the line above",
                `${Number(gstByBm) || 0} %`,
                inr(gstValueByBm)
              )}
            </tbody>
            <tfoot>
              <tr className="gdoc-grand">
                <th scope="row">Gold loan amount</th>
                <td />
                <td className="gdoc-num">{inr(eligibleLoanAmountByBm)}</td>
              </tr>
            </tfoot>
          </table>

          {showGplCashAndInsurance && (
            <div className="gdoc-cash">
              <Form.Check
                id="gdoc-needCash"
                type="checkbox"
                label="Customer needs a cash loan too"
                checked={needCashLoan}
                onChange={(e) => setNeedCashLoan(e.target.checked)}
              />
              {needCashLoan && (
                <div className="gdoc-field gdoc-cash-amount">
                  <label htmlFor="gdoc-cashAmount">Cash loan amount {required}</label>
                  {unitInput(
                    "gdoc-cashAmount",
                    "₹",
                    cashLoanAmount || "",
                    (e) => setCashLoanAmount(Number(e.target.value)),
                    "0",
                    true
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="gdoc-col gdoc-col-right">
          <h6 className="gdoc-section">Charges</h6>
          <table className="gdoc-table">
            <thead>
              <tr>
                <th>Charge</th>
                <th className="gdoc-col-paid">Paid</th>
                <th className="gdoc-num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {chargeRow(
                "Processing fee",
                `${fundingAgencyData?.gplProcessingFeePercentage || 2}% of loan`,
                processingCharge,
                isProcessingChargePaid,
                setIsProcessingChargePaid
              )}
              {chargeRow(
                "GST",
                `${fundingAgencyData?.gplGstPercentage || 18}% of processing fee`,
                gst,
                isGstPaid,
                setIsGstPaid
              )}
              {showGplSecurityDeposit &&
                chargeRow("Security deposit", "One EMI", securityDeposit, false, null)}
              {showGplCashAndInsurance &&
                chargeRow(
                  "Insurance",
                  gplInsuranceRate,
                  insuranceAmount,
                  isInsuranceAmountPaid,
                  setIsInsuranceAmountPaid
                )}
            </tbody>
            <tfoot>
              <tr className="gdoc-grand">
                <th scope="row">Total loan amount</th>
                <td />
                <td className="gdoc-num">{inr(totalLoanAmountByBm)}</td>
              </tr>
            </tfoot>
          </table>

          <h6 className="gdoc-section gdoc-section-gap">Repayment</h6>
          <div className="gdoc-pair">
            <div className="gdoc-field">
              <label htmlFor="gdoc-emiDay">EMI day of month {required}</label>
              <Form.Control
                id="gdoc-emiDay"
                type="number"
                min="1"
                max="31"
                placeholder="1 - 31"
                value={emiDateByBranchManager || ""}
                onChange={handleEmiDayChange}
                onKeyDown={blockNonDayKeys}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>
            <div className="gdoc-field">
              <label htmlFor="gdoc-startDate">Installment start date</label>
              <Form.Control
                id="gdoc-startDate"
                type="date"
                value={installmentStartDate || ""}
                min={installmentDateMin}
                max={installmentDateMax}
                onChange={(e) => setInstallmentStartDate(e.target.value || null)}
              />
            </div>
          </div>
          <div className="gdoc-hint gdoc-hint-block">
            The start date is suggested from the EMI day. Change it only if the customer
            agreed to start on a different date.
          </div>
        </section>
      </div>

      {validationError && (
        <div className="alert alert-danger gdoc-alert">{validationError}</div>
      )}
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className={isGpl ? "gdoc-modal" : undefined}
      dialogClassName={isGpl ? "gdoc-dialog" : undefined}
    >
      <Modal.Header closeButton>
        <Modal.Title>Generate Document</Modal.Title>
        {isGpl && <span className="gdoc-subtitle">GP Loan</span>}
      </Modal.Header>
      <Modal.Body>
        {isGpl ? gplBody : (
        <>
        {message}
        <div className="my-3">
          {loanType === "Business Loan" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Proposed Loan Amount</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter Proposed Loan Amount"
                  value={proposedLoanAmount || ""}
                  onChange={(e) => setProposedLoanAmount(Number(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.preventDefault()}
                  onFocus={(e) => {
                    e.target.addEventListener(
                      "wheel",
                      (e) => e.preventDefault(),
                      { passive: false }
                    );
                  }}
                  onBlur={(e) => {
                    e.target.removeEventListener("wheel", (e) =>
                      e.preventDefault()
                    );
                  }}
                  step="1"
                  readOnly
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Sanctioned Loan Amount</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter Sanctioned Loan Amount"
                  value={sanctionedLoanAmount || ""}
                  onChange={(e) => setSanctionedLoanAmount(Number(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.preventDefault()}
                  onFocus={(e) => {
                    e.target.addEventListener(
                      "wheel",
                      (e) => e.preventDefault(),
                      { passive: false }
                    );
                  }}
                  onBlur={(e) => {
                    e.target.removeEventListener("wheel", (e) =>
                      e.preventDefault()
                    );
                  }}
                  step="1"
                  readOnly
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  Processing Charge (
                  {loanType === "Business Loan"
                    ? (fundingAgencyData?.blProcessingFeePercentage || 0)
                    : (fundingAgencyData?.gplProcessingFeePercentage || 2)}% loan amount)
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter Processing Charge"
                  value={processingCharge || ""}
                  onChange={(e) => setProcessingCharge(Number(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.preventDefault()}
                  onFocus={(e) => {
                    e.target.addEventListener(
                      "wheel",
                      (e) => e.preventDefault(),
                      { passive: false }
                    );
                  }}
                  onBlur={(e) => {
                    e.target.removeEventListener("wheel", (e) =>
                      e.preventDefault()
                    );
                  }}
                  step="1"
                  readOnly
                />
                <Form.Check
                  type="checkbox"
                  label="Processing Charge Paid"
                  checked={isProcessingChargePaid}
                  onChange={(e) => setIsProcessingChargePaid(e.target.checked)}
                  className="mt-2"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  GST (
                  {loanType === "Business Loan"
                    ? (fundingAgencyData?.blGstPercentage || 0)
                    : (fundingAgencyData?.gplGstPercentage || 18)}% processing charge)
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter GST"
                  value={gst || ""}
                  onChange={(e) => setGst(Number(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.preventDefault()}
                  onFocus={(e) => {
                    e.target.addEventListener(
                      "wheel",
                      (e) => e.preventDefault(),
                      { passive: false }
                    );
                  }}
                  onBlur={(e) => {
                    e.target.removeEventListener("wheel", (e) =>
                      e.preventDefault()
                    );
                  }}
                  step="1"
                  readOnly
                />
                <Form.Check
                  type="checkbox"
                  label="GST Paid"
                  checked={isGstPaid}
                  onChange={(e) => setIsGstPaid(e.target.checked)}
                  className="mt-2"
                />
              </Form.Group>
              {(!fundingAgencyData || fundingAgencyData.blSecurityDepositEnabled !== false) && (
                <Form.Group className="mb-3">
                  <Form.Label>Security Deposit (1 month EMI amount)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter Security Deposit"
                    value={securityDeposit === 0 ? 0 : (securityDeposit || "")}
                    onChange={(e) => setSecurityDeposit(e.target.value === "" ? 0 : Number(e.target.value))}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) => e.preventDefault()}
                    onFocus={(e) => {
                      e.target.addEventListener(
                        "wheel",
                        (e) => e.preventDefault(),
                        { passive: false }
                      );
                    }}
                    onBlur={(e) => {
                      e.target.removeEventListener("wheel", (e) =>
                        e.preventDefault()
                      );
                    }}
                    step="1"
                    readOnly
                  />
                  <Form.Check
                    type="checkbox"
                    label="Security Deposit Paid"
                    checked={isSecurityDepositPaid}
                    onChange={(e) => setIsSecurityDepositPaid(e.target.checked)}
                    className="mt-2"
                  />
                </Form.Group>
              )}
              {(!fundingAgencyData || fundingAgencyData.blInsuranceEnabled !== false) && (
                <>
                  <Form.Check
                    type="checkbox"
                    label="Is Loan Insured?"
                    checked={isLoanInsured}
                    onChange={(e) => setIsLoanInsured(e.target.checked)}
                    className="mb-3"
                  />
                  {isLoanInsured && (
                    <Form.Group className="mb-3">
                      <Form.Label>Insurance Amount</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter Insurance Amount"
                        value={insuranceAmount || ""}
                        onChange={(e) => setInsuranceAmount(Number(e.target.value))}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.preventDefault()}
                        onFocus={(e) => {
                          e.target.addEventListener(
                            "wheel",
                            (e) => e.preventDefault(),
                            { passive: false }
                          );
                        }}
                        onBlur={(e) => {
                          e.target.removeEventListener("wheel", (e) =>
                            e.preventDefault()
                          );
                        }}
                        step="1"
                        readOnly
                      />
                      <Form.Check
                        type="checkbox"
                        label="Insurance Amount Paid"
                        checked={isInsuranceAmountPaid}
                        onChange={(e) => setIsInsuranceAmountPaid(e.target.checked)}
                        className="mt-2"
                      />
                    </Form.Group>
                  )}
                </>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Loan Amount To Release</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter Loan Amount To Release"
                  value={amountToRelease || ""}
                  onChange={(e) => setAmountToRelease(Number(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.preventDefault()}
                  onFocus={(e) => {
                    e.target.addEventListener(
                      "wheel",
                      (e) => e.preventDefault(),
                      { passive: false }
                    );
                  }}
                  onBlur={(e) => {
                    e.target.removeEventListener("wheel", (e) =>
                      e.preventDefault()
                    );
                  }}
                  step="1"
                  readOnly
                />
              </Form.Group>
            </>
          )}
          <Form.Group className="mb-3">
            <Form.Label>EMI Day of Month</Form.Label>
            {/*
              This field means "which day of the month the EMI recurs on" - it is
              applied to every month of the loan's tenure (see getEmiDay / setDate
              usage across utils.js and getForeclosureDataForBm.js). It is NOT a
              specific calendar date, so a date picker was the wrong control: it
              always submits a full "YYYY-MM-DD" string, which crashed foreclosure
              (Bug 7, ER_WRONG_VALUE) and left the column holding two different
              formats. A day-only number input can never produce that string.
            */}
            <Form.Control
              type="number"
              min="1"
              max="31"
              placeholder="Day of month (1-31)"
              value={emiDateByBranchManager || ""}
              onChange={handleEmiDayChange}
              onKeyDown={blockNonDayKeys}
            />
          </Form.Group>
          {/*
            The date the first instalment is actually collected. Separate from
            the day-of-month field above, and stored in its own column, because
            one column holding both a day number and a full date is what caused
            Bug 7. JLG is excluded: those loans repay on the centre meeting day.
          */}
          {loanType !== "JLG Loan" && (
            <Form.Group className="mb-3">
              <Form.Label>Installment Start Date</Form.Label>
              <Form.Control
                type="date"
                value={installmentStartDate || ""}
                min={installmentDateMin}
                max={installmentDateMax}
                onChange={(e) => setInstallmentStartDate(e.target.value || null)}
              />
              <Form.Text className="text-muted">
                Auto-suggested from the EMI day. Change it only if the customer
                agreed to start on a different date.
              </Form.Text>
            </Form.Group>
          )}
          {
            validationError && (
              <p className="text-danger">{validationError}</p>
            )
          }
        </div>
        </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isGpl && <span className="gdoc-legend"><span className="gdoc-required">*</span> Mandatory field</span>}
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleClick}>
          Generate Document
        </Button>
      </Modal.Footer>
    </Modal >
  );
};

export default GenerateDocumentBranchManagerModal;